import React, { useCallback, useEffect, useState } from "react";
import { apiUrl } from "../taskpane";
import { confirmDialog } from "../utils/confirmDialog";

// The manifest stamps ?v=<Version> on every taskpane URL (webpack.config.js), so the
// query string tells us which manifest this add-in was installed from.
const INSTALLED_VERSION = new URLSearchParams(window.location.search).get("v") || "";
const VERSION_PATTERN = /^\d+(\.\d+){1,3}$/;

const parseVersion = (version) => version.split(".").map((part) => Number(part));

export function isOutdated(installed, latest) {
  if (!VERSION_PATTERN.test(installed)) return true;
  const a = parseVersion(installed);
  const b = parseVersion(latest);
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    const diff = (b[i] || 0) - (a[i] || 0);
    if (diff !== 0) return diff > 0;
  }
  return false;
}

async function fetchLatestVersion() {
  const res = await fetch(`${window.location.origin}/manifest.xml?ts=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`სერვერმა დააბრუნა ${res.status}`);
  const doc = new DOMParser().parseFromString(await res.text(), "application/xml");
  const version = doc.getElementsByTagName("Version")[0]?.textContent?.trim();
  if (!version) throw new Error("სერვერის manifest-ში ვერსია ვერ მოიძებნა");
  return version;
}

function openInBrowser(url) {
  const ui = typeof Office !== "undefined" ? Office?.context?.ui : undefined;
  if (ui && typeof ui.openBrowserWindow === "function") {
    ui.openBrowserWindow(url);
  } else {
    window.open(url, "_blank", "noopener");
  }
}

const card = {
  background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.15)",
  borderRadius: 10, padding: "14px 16px", marginBottom: 12, fontSize: 13, lineHeight: 1.55,
};
const button = {
  borderRadius: 6, padding: "8px 14px", fontSize: 12, cursor: "pointer",
  border: "1px solid rgba(148,163,184,0.25)", background: "var(--surface-soft)", color: "var(--text-primary)",
};

export default function AddinUpdatePage({ onBack }) {
  const [status, setStatus] = useState("checking");
  const [latestVersion, setLatestVersion] = useState("");
  const [error, setError] = useState("");

  const check = useCallback(async () => {
    setStatus("checking");
    setError("");
    try {
      const latest = await fetchLatestVersion();
      setLatestVersion(latest);
      setStatus(isOutdated(INSTALLED_VERSION, latest) ? "outdated" : "current");
    } catch (err) {
      setError(err?.message || "შემოწმება ვერ მოხერხდა");
      setStatus("error");
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const startUpdate = async () => {
    const confirmed = await confirmDialog(
      "განახლების დროს Excel დაიხურება და თავიდან გაიხსნება. შენახვას Excel თავად გკითხავთ. გავაგრძელოთ?",
      { confirmLabel: "განახლება", cancelLabel: "გაუქმება", danger: false }
    );
    if (!confirmed) return;
    openInBrowser(apiUrl("addin-update"));
    setStatus("launched");
  };

  const installedLabel = VERSION_PATTERN.test(INSTALLED_VERSION) ? INSTALLED_VERSION : "ძველი ვერსია";

  return (
    <div
      data-no-translate="true"
      style={{
        minHeight: "100vh", background: "var(--page-gradient)", padding: "20px 16px",
        color: "var(--text-primary)", fontFamily: "Arial, sans-serif",
      }}
    >
      {onBack && <button className="link" onClick={onBack}>← უკან</button>}
      <h2 style={{ marginTop: 16, marginBottom: 12 }}>დანამატის განახლება</h2>

      <div style={card}>
        <div>დაყენებული ვერსია: <strong>{installedLabel}</strong></div>
        <div>სერვერზე: <strong>{latestVersion || "…"}</strong></div>
      </div>

      {status === "checking" && <div style={card}>მოწმდება…</div>}

      {status === "current" && (
        <div style={{ ...card, borderColor: "rgba(74,222,128,0.4)" }}>
          ✅ დანამატი განახლებულია.
        </div>
      )}

      {status === "outdated" && (
        <div style={{ ...card, borderColor: "rgba(250,204,21,0.5)" }}>
          <div style={{ marginBottom: 10 }}>⬆️ ხელმისაწვდომია ახალი ვერსია. განვაახლოთ?</div>
          <button className="primary" style={{ ...button, border: "none" }} onClick={startUpdate}>
            განახლება
          </button>
        </div>
      )}

      {status === "launched" && (
        <div style={card}>
          <div style={{ marginBottom: 8 }}>ბრაუზერში გაიხსნა განახლების გვერდი.</div>
          <ol style={{ margin: "0 0 0 18px", padding: 0 }}>
            <li>ბრაუზერის კითხვაზე <b>Open Costy Add-in Updater?</b> დააჭირეთ <b>Open</b>.</li>
            <li>დაადასტურეთ განახლება — Excel დაიხურება და თავიდან გაიხსნება.</li>
          </ol>
          <div style={{ marginTop: 10, color: "var(--text-muted)", fontSize: 12 }}>
            თუ არაფერი მოხდა, ჩამოტვირთეთ ინსტალერი ადმინ პანელიდან და გაუშვით ხელახლა.
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button style={button} onClick={startUpdate}>ხელახლა ცდა</button>
            <button style={button} onClick={() => openInBrowser(apiUrl("admin-panel"))}>ადმინ პანელი</button>
          </div>
        </div>
      )}

      {status === "error" && (
        <div style={{ ...card, borderColor: "rgba(248,113,113,0.5)" }}>
          <div style={{ marginBottom: 10 }}>შეცდომა: {error}</div>
          <button style={button} onClick={check}>ხელახლა შემოწმება</button>
        </div>
      )}

      {status !== "checking" && status !== "error" && (
        <button style={{ ...button, marginTop: 4 }} onClick={check}>ხელახლა შემოწმება</button>
      )}
    </div>
  );
}
